// src/components/providerAssignments/ManageMemberProvidersModal.tsx
//
// Phase 12.7 — Head-only modal to manage a single member's provider
// pins per channel. Dropdown options come from the Circle's EFFECTIVE
// provider catalog (Phase 12.4 semantics) filtered by channel type.
// "None" clears the pin.
//
// The backend Head-of-Team gate (`authorizeCircleWrite`) is
// authoritative — this modal is only rendered when
// `circle.canManageAsHead === true`, but a non-Head who fires the
// PUT/DELETE request directly still gets 403.
//
// Voice AI and WhatsApp channels are surfaced only when the caller
// explicitly opts in (product decision per Phase 12.7 spec — no fake
// controls for channels that have no user-driven Outbound path).

import { useEffect, useMemo, useState } from 'react';
import {
  useDeleteCircleMemberProviderMutation,
  useGetCircleMemberProvidersQuery,
  useGetCircleProvidersQuery,
  usePutCircleMemberProviderMutation,
  type MemberAssignmentChannel,
  type MemberProviderAssignment,
} from '../../api/circlesApi';
import type { IntegrationConnection } from '../../types/api';

interface ChannelOption {
  channel: MemberAssignmentChannel;
  label: string;
  hint?: string;
}

const CHANNEL_LABELS: Record<MemberAssignmentChannel, string> = {
  voice: 'Voice',
  sms: 'SMS',
  email: 'Email',
  whatsapp: 'WhatsApp',
  voice_ai: 'Voice AI',
};

// Phase 12.7 default channel matrix: expose only channels where an
// Outbound user-driven runtime path exists today. Voice AI and
// WhatsApp are omitted (Voice AI is campaign-owned; WhatsApp lacks a
// user-driven Circle-aware entry).
const DEFAULT_CHANNELS: ChannelOption[] = [
  { channel: 'voice', label: CHANNEL_LABELS.voice },
  { channel: 'sms', label: CHANNEL_LABELS.sms },
  { channel: 'email', label: CHANNEL_LABELS.email, hint: 'Outbound-backed email only' },
];

export interface ManageMemberProvidersModalProps {
  circleId: string;
  memberUserId: string;
  memberDisplayName: string;
  onClose: () => void;
}

interface DraftRow {
  channel: MemberAssignmentChannel;
  /** '' = none / unassign. */
  outboundProviderId: string;
  /** The id that was persisted (if any) so we can decide upsert-vs-delete. */
  original: string;
}

export default function ManageMemberProvidersModal({
  circleId,
  memberUserId,
  memberDisplayName,
  onClose,
}: ManageMemberProvidersModalProps) {
  const memberQuery = useGetCircleMemberProvidersQuery({ circleId, memberUserId });
  const effectiveQuery = useGetCircleProvidersQuery(circleId);
  const [putMemberProvider, putState] = usePutCircleMemberProviderMutation();
  const [deleteMemberProvider, delState] = useDeleteCircleMemberProviderMutation();

  const [draft, setDraft] = useState<DraftRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string | null>>({});

  const initialAssignments: MemberProviderAssignment[] =
    memberQuery.data?.assignments ?? [];

  // Initialize the draft once the initial assignments load.
  useEffect(() => {
    if (draft !== null) return;
    if (memberQuery.isLoading || memberQuery.isFetching) return;
    const initial = DEFAULT_CHANNELS.map<DraftRow>(({ channel }) => {
      const existing = initialAssignments.find((a) => a.channelType === channel);
      return {
        channel,
        outboundProviderId: existing?.outboundProviderId ?? '',
        original: existing?.outboundProviderId ?? '',
      };
    });
    setDraft(initial);
  }, [
    draft,
    memberQuery.isLoading,
    memberQuery.isFetching,
    initialAssignments,
  ]);

  // Build channel → available providers from the Circle effective set.
  const providersByChannel = useMemo(() => {
    const map = new Map<
      MemberAssignmentChannel,
      Array<{ id: string; label: string; status: string | null }>
    >();
    const integrations: IntegrationConnection[] =
      effectiveQuery.data?.integrations ?? [];
    for (const integration of integrations) {
      const settings = (integration.settings ?? {}) as {
        sync?: {
          channels?: Array<{
            outboundProviderId?: string | null;
            type?: string | null;
            displayName?: string | null;
            status?: string | null;
          } | null>;
        };
      };
      const channels = Array.isArray(settings.sync?.channels)
        ? settings.sync!.channels!
        : [];
      for (const ch of channels) {
        const pid = ch?.outboundProviderId;
        const type = ch?.type;
        if (typeof pid !== 'string' || !pid) continue;
        if (
          type !== 'voice' &&
          type !== 'sms' &&
          type !== 'email' &&
          type !== 'whatsapp' &&
          type !== 'voice_ai'
        ) {
          continue;
        }
        const label =
          ch?.displayName || integration.displayName || integration.appKey || 'Provider';
        const list = map.get(type as MemberAssignmentChannel) ?? [];
        list.push({ id: pid, label, status: ch?.status ?? integration.status ?? null });
        map.set(type as MemberAssignmentChannel, list);
      }
    }
    return map;
  }, [effectiveQuery.data]);

  const isSubmitting = putState.isLoading || delState.isLoading;

  const handleSave = async () => {
    if (!draft) return;
    setError(null);
    const nextRowErrors: Record<string, string | null> = {};

    for (const row of draft) {
      if (row.outboundProviderId === row.original) continue;
      try {
        if (row.outboundProviderId === '') {
          await deleteMemberProvider({
            circleId,
            memberUserId,
            channelType: row.channel,
          }).unwrap();
        } else {
          await putMemberProvider({
            circleId,
            memberUserId,
            channelType: row.channel,
            outboundProviderId: row.outboundProviderId,
          }).unwrap();
        }
        nextRowErrors[row.channel] = null;
      } catch (err: any) {
        const msg =
          err?.data?.error ||
          err?.data?.message ||
          `Failed to save ${CHANNEL_LABELS[row.channel]}.`;
        nextRowErrors[row.channel] = msg;
      }
    }

    setRowErrors(nextRowErrors);
    // Close only if no row errored.
    const anyError = Object.values(nextRowErrors).some((v) => !!v);
    if (!anyError) onClose();
  };

  const hasChanges =
    draft?.some((r) => r.outboundProviderId !== r.original) ?? false;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="manage-member-providers-modal"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Manage providers
            </h3>
            <p className="text-xs text-slate-500">
              {memberDisplayName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-medium text-slate-500 hover:text-slate-700"
            data-testid="manage-member-providers-close"
          >
            Close
          </button>
        </div>

        {memberQuery.isError && (
          <div
            role="alert"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
            data-testid="manage-member-providers-load-error"
          >
            Failed to load current assignments.
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
          >
            {error}
          </div>
        )}

        <div className="mt-4 space-y-3">
          {(draft ?? DEFAULT_CHANNELS.map((c) => ({
            channel: c.channel,
            outboundProviderId: '',
            original: '',
          }))).map((row) => {
            const options = providersByChannel.get(row.channel) ?? [];
            const channelMeta = DEFAULT_CHANNELS.find((c) => c.channel === row.channel);
            const rowError = rowErrors[row.channel];
            return (
              <div key={row.channel} className="space-y-1">
                <label
                  className="text-xs font-medium text-slate-700"
                  htmlFor={`manage-provider-${row.channel}`}
                >
                  {CHANNEL_LABELS[row.channel]}
                  {channelMeta?.hint && (
                    <span className="ml-1 font-normal text-slate-400">
                      · {channelMeta.hint}
                    </span>
                  )}
                </label>
                <select
                  id={`manage-provider-${row.channel}`}
                  className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                  value={row.outboundProviderId}
                  disabled={isSubmitting || !draft}
                  onChange={(e) => {
                    if (!draft) return;
                    setDraft(
                      draft.map((r) =>
                        r.channel === row.channel
                          ? { ...r, outboundProviderId: e.target.value }
                          : r,
                      ),
                    );
                  }}
                  data-testid={`manage-provider-select-${row.channel}`}
                >
                  <option value="">None</option>
                  {options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                      {o.status && o.status !== 'active' && o.status !== 'connected'
                        ? ` (${o.status})`
                        : ''}
                    </option>
                  ))}
                </select>
                {rowError && (
                  <p
                    className="text-[10px] font-medium text-red-600"
                    data-testid={`manage-provider-error-${row.channel}`}
                  >
                    {rowError}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
            data-testid="manage-member-providers-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || isSubmitting}
            className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            data-testid="manage-member-providers-save"
          >
            {isSubmitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
